<template>
  <el-dialog
    title="迁移厂家抓拍图"
    :visible.sync="dialogVisible"
    width="920px"
    top="6vh"
    append-to-body
    :close-on-click-modal="false"
    @open="onOpen"
    @close="onClose"
  >
    <div class="mig-hint">
      仅列出 <code>PicUrl</code> 仍含 <strong>ist-falcon</strong> 的进出记录。迁移会下载厂家图并上传本校 OSS，再回写路径。
      <span v-if="total != null">当前待处理 <strong>{{ total }}</strong> 条。</span>
    </div>

    <el-table
      ref="table"
      :data="rows"
      v-loading="listLoading"
      height="420"
      size="small"
      stripe
      @selection-change="onSelectionChange"
    >
      <el-table-column type="selection" width="45"></el-table-column>
      <el-table-column label="类型" width="80">
        <template slot-scope="scope">
          <el-tag :type="scope.row.recordType === 1 ? 'success' : 'warning'" size="mini">
            {{ scope.row.recordTypeName || (scope.row.recordType === 1 ? '入场' : '出场') }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="plateNo" label="车牌号" width="120"></el-table-column>
      <el-table-column label="事件时间" width="170">
        <template slot-scope="scope">{{ formatDate(scope.row.eventTime) }}</template>
      </el-table-column>
      <el-table-column label="厂家图张数" width="100" align="center">
        <template slot-scope="scope">{{ scope.row.externalPicCount }}</template>
      </el-table-column>
      <el-table-column prop="picUrl" label="当前 PicUrl" min-width="260" show-overflow-tooltip></el-table-column>
    </el-table>

    <div class="mig-pager">
      <el-pagination
        layout="total, prev, pager, next"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="total"
        @current-change="loadPreview"
      ></el-pagination>
    </div>

    <div v-if="executeSummary" class="mig-summary">{{ executeSummary }}</div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false" :disabled="migrating">关闭</el-button>
      <el-button
        type="warning"
        :loading="migrating"
        :disabled="listLoading || total === 0"
        @click="handleMigrate(true)"
      >迁移全部待处理</el-button>
      <el-button
        type="primary"
        :loading="migrating"
        :disabled="listLoading || selectedRows.length === 0"
        @click="handleMigrate(false)"
      >迁移选中（{{ selectedRows.length }}）</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { getParkingExternalPicPending, migrateParkingExternalPics } from '@/api/parking'

/**
 * 厂家抓拍图（ist-falcon）转本校 OSS。创建于 2026-09-18
 */
export default {
  name: 'ParkingExternalPicMigrateDialog',
  props: {
    visible: { type: Boolean, default: false },
    schoolId: { type: [Number, String], default: null }
  },
  data() {
    return {
      listLoading: false,
      migrating: false,
      rows: [],
      selectedRows: [],
      page: 1,
      pageSize: 20,
      total: 0,
      executeSummary: ''
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  methods: {
    onOpen() {
      this.page = 1
      this.executeSummary = ''
      this.selectedRows = []
      this.loadPreview(1)
    },
    onClose() {
      this.rows = []
      this.selectedRows = []
      this.executeSummary = ''
    },
    onSelectionChange(rows) {
      this.selectedRows = rows || []
    },
    /**
     * 加载待迁移列表
     * @param {number} [page]
     */
    async loadPreview(page) {
      if (!this.schoolId) {
        this.$message.warning('请先选择学校')
        return
      }
      if (page) this.page = page
      this.listLoading = true
      try {
        const res = await getParkingExternalPicPending({
          schoolId: this.schoolId,
          page: this.page,
          pageSize: this.pageSize
        })
        if (!res.data || !res.data.success) {
          this.$message.error((res.data && res.data.msg) || '加载失败')
          this.rows = []
          this.total = 0
          return
        }
        const resp = res.data.response || {}
        const raw = resp.data || resp.Data || []
        this.rows = (raw || []).map(r => ({
          id: r.Id != null ? r.Id : r.id,
          recordType: r.RecordType != null ? r.RecordType : r.recordType,
          recordTypeName: r.RecordTypeName || r.recordTypeName,
          plateNo: r.PlateNo || r.plateNo || '',
          eventTime: r.EventTime || r.eventTime,
          picUrl: r.PicUrl || r.picUrl || '',
          externalPicCount: r.ExternalPicCount != null ? r.ExternalPicCount : (r.externalPicCount || 0)
        }))
        this.total = resp.dataCount != null ? resp.dataCount : (resp.DataCount || 0)
      } catch (e) {
        this.$message.error('加载待处理数据失败')
        this.rows = []
        this.total = 0
      } finally {
        this.listLoading = false
      }
    },
    /**
     * 执行迁移
     * @param {boolean} migrateAll 是否迁移本校全部待处理
     */
    async handleMigrate(migrateAll) {
      if (!this.schoolId) return
      const count = migrateAll ? this.total : this.selectedRows.length
      if (!count) {
        this.$message.warning('没有可迁移的数据')
        return
      }
      const tip = migrateAll
        ? `确认迁移本校全部待处理记录（约 ${this.total} 条）？`
        : `确认迁移选中的 ${count} 条？`
      try {
        await this.$confirm(tip, '确认迁移', { type: 'warning' })
      } catch (e) {
        return
      }

      this.migrating = true
      this.executeSummary = ''
      try {
        const payload = {
          schoolId: this.schoolId,
          ids: migrateAll ? [] : this.selectedRows.map(r => r.id)
        }
        const res = await migrateParkingExternalPics(payload)
        if (!res.data || !res.data.success) {
          this.$message.error((res.data && res.data.msg) || '迁移失败')
          return
        }
        const result = res.data.response || {}
        const successCount = result.SuccessCount != null ? result.SuccessCount : (result.successCount || 0)
        const skipCount = result.SkipCount != null ? result.SkipCount : (result.skipCount || 0)
        const failCount = result.FailCount != null ? result.FailCount : (result.failCount || 0)
        this.executeSummary = `完成：成功 ${successCount}，跳过 ${skipCount}，失败 ${failCount}`
        if (failCount > 0) {
          const msgs = result.FailMessages || result.failMessages || []
          if (msgs.length) this.executeSummary += `；示例：${msgs[0]}`
          this.$message.warning(this.executeSummary)
        } else {
          this.$message.success(this.executeSummary)
        }
        this.$emit('done')
        await this.loadPreview(1)
      } catch (e) {
        this.$message.error('迁移请求失败，请稍后重试')
      } finally {
        this.migrating = false
      }
    },
    formatDate(val) {
      if (!val) return ''
      const d = new Date(val)
      if (isNaN(d.getTime())) return val
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    }
  }
}
</script>

<style scoped>
.mig-hint {
  margin-bottom: 12px;
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
}
.mig-hint code {
  padding: 0 4px;
  background: #f5f7fa;
  border-radius: 3px;
}
.mig-pager {
  margin-top: 12px;
  text-align: right;
}
.mig-summary {
  margin-top: 10px;
  font-size: 13px;
  color: #909399;
}
</style>
